import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Editor } from "@tinymce/tinymce-react";
import secureLocalStorage from "react-secure-storage";

const AdditionalInfo = () => {
  const [loading, setLoading] = useState(true);

  // Section States
  const [summary, setSummary] = useState("");
  const [objective, setObjective] = useState("");
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState("");
  const [hobbies, setHobbies] = useState("");
  const [industries, setIndustries] = useState("");
  const [salary, setSalary] = useState({});
  const [references, setReferences] = useState([]);
  const [refForm, setRefForm] = useState({});
  const [languages, setLanguages] = useState([]);
  const [langForm, setLangForm] = useState({});
  const [activities, setActivities] = useState("");
  const [declaration, setDeclaration] = useState("");

  const [editRefIndex, setEditRefIndex] = useState(null);
  const [editLangIndex, setEditLangIndex] = useState(null);

  // ✅ Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
			setLoading(true);
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				user: secureLocalStorage.getItem("user"),
				token:secureLocalStorage.getItem("token"),
			};
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/additional-info", payload);
       
			const data = res.data || {};
        
			setSummary(data.summary || "");
			setObjective(data.objective || "");
			setSkills(data.skills || []);
			setHobbies(data.hobbies || "");
			setIndustries(data.industries || "");
			setSalary(data.salary || {});
			setReferences(data.references || []);
			setLanguages(data.languages || []);
			setActivities(data.activities || "");
			setDeclaration(data.declaration || "");
      } catch (err) {
        console.error("Error loading additional info:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

	// ✅ Add skill
	const handleAddSkill = async () => {
	  if (skillInput.trim()) {
		try {
		  const payload = new FormData();
		  payload.append("skill", skillInput.trim());
		  payload.append("userId", import.meta.env.VITE_API_USER);
		  payload.append("password", import.meta.env.VITE_API_PASS);
		  payload.append("user", secureLocalStorage.getItem("user"));
		  payload.append("token", secureLocalStorage.getItem("token"));
		  const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		  });
		  // Send to backend
		  const res = await api.post("/update-skills", payload);

		  // Update state with the new skill returned from the backend
		  setSkills((prev) => [...prev, skillInput]);
		  setSkillInput("");

		} catch (error) {
		  console.error("Error adding skill:", error);
		  alert("Failed to add skill!");
		}
	  }
	};

	const handleRemoveSkill = async (index) => {
	  try {
		const skillToRemove = skills[index]; // Get the skill value (or ID if your backend uses IDs)

		// Optional: Optimistically update UI first
		setSkills(skills.filter((_, i) => i !== index));

		// Prepare payload
		const payload = new FormData();
		payload.append("skill", skillToRemove);
		payload.append("userId", import.meta.env.VITE_API_USER);
		payload.append("password", import.meta.env.VITE_API_PASS);
		payload.append("user", secureLocalStorage.getItem("user"));
		payload.append("token", secureLocalStorage.getItem("token"));
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		// Send DELETE request to backend
		await api.post("/skills-delete", payload);

		console.log("Skill removed successfully!");
	  } catch (error) {
		console.error("Error removing skill:", error);
		alert("Failed to remove skill!");
	  }
	};
  // ✅ Handle input change
  const handleChange = (e, setter) => {
    const { name, value } = e.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  if (loading)
    return (
      <div className="text-center text-blue-600 py-8">
        Loading Additional Information...
      </div>
    );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-md space-y-10"
    >
      <h2 className="text-2xl font-semibold text-[#1A2B63] mb-4">
        Additional Information
      </h2>

      {/* ====================== Career Summary ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">
          Career Summary
        </h3>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("summary", summary);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));	
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/career-summary`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Career Summary updated!");
			} catch (error) {
			  console.error("Error updating career summary:", error);
			  alert("Failed to update Career Summary!");
			}
		  }}
		  className="space-y-2"
		>
			<Editor
			  apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
			  value={summary}
			  onEditorChange={setSummary}
			  init={{
				height: 200,
				menubar: false,
				plugins: ["link lists code"],
				toolbar:
				  "bold italic underline | bullist numlist | alignleft aligncenter alignright | link | code",
			  }}
			/>
			<div className="text-right">
			  <Button type="submit" className="bg-blue-600 text-white">
				Update Summary
			  </Button>
			</div>
		</form>
      </section>

      {/* ====================== Career Objective ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">
          Career Objective
        </h3>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("objective", objective);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/career-objective`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Career objective updated!");
			} catch (error) {
			  console.error("Error updating career summary:", error);
			  alert("Failed to update Career Summary!");
			}
		  }}
		  className="space-y-2"
		>
		
        <Editor
          apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
          value={objective}
          onEditorChange={setObjective}
          init={{
            height: 200,
            menubar: false,
            plugins: ["link lists code"],
            toolbar:
              "bold italic underline | bullist numlist | alignleft aligncenter alignright | link | code",
          }}
        />
		<div className="text-right">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Objective
			</Button>
		  </div>
		</form>
      </section>

      {/* ====================== Skills ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-3">Skills</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            placeholder="Enter a skill..."
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 flex-1"
          />
          <Button className="bg-blue-600 text-white" onClick={handleAddSkill}>
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {skills.map((skill, i) => (
            <span
              key={i}
              className="bg-blue-100 text-[#1A2B63] px-3 py-1 rounded-full text-sm flex items-center gap-2"
            >
              {skill}
              <button
                type="button"
                onClick={() => handleRemoveSkill(i)}
                className="text-red-500 hover:text-red-700 font-bold"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      </section>

      {/* ====================== Hobbies & Interests ====================== */}
      <section>
        <label className="text-gray-700 font-semibold text-sm mb-1 block">
          Hobbies & Interests
        </label>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("hobbies", hobbies);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/update-hobbies`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Hobbies updated!");
			} catch (error) {
			  console.error("Error updating Hobbies:", error);
			  alert("Failed to update Hobbies!");
			}
		  }}
		  className="space-y-2"
		>
		
        <textarea
          value={hobbies}
          onChange={(e) => setHobbies(e.target.value)}
          className="w-full border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
        />
		<div className="text-right">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Hobbies
			</Button>
		  </div>
		</form>
      </section>

      {/* ====================== Preferred Industries ====================== */}
      <section>
        <label className="text-gray-700 font-semibold text-sm mb-1 block">
          Preferred Industries
        </label>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("industries", industries);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/update-industries`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Industries updated!");
			} catch (error) {
			  console.error("Error updating Industries:", error);
			  alert("Failed to update Industries!");
			}
		  }}
		  className="space-y-2"
		>
		
        <textarea
          value={industries}
          onChange={(e) => setIndustries(e.target.value)}
          className="w-full border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
        />
		<div className="text-right">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Industries
			</Button>
		  </div>
		</form>
      </section>

      {/* ====================== Salary Info ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">
          Salary Information
        </h3>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();

			  // Append salary info
			  Object.entries(salary).forEach(([key, value]) => {
				payload.append(key, value);
			  });

			  // Append authentication details
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			  });
			  const response = await api.post("/update-salary", payload);
			 

			  alert("Salary information updated!");
			} catch (error) {
			  console.error("Error updating salary info:", error);
			  alert("Failed to update salary information!");
			}
		  }}
		  className=""
		>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Current Salary (BDT/Month)", name: "currentSalary" },
            { label: "Expected Salary (BDT/Month)", name: "expectedSalary" },
            { label: "Notice Period", name: "noticePeriod" },
          ].map((field, i) => (
            <div key={i} className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">
                {field.label}
              </label>
              <input
                name={field.name}
                value={salary[field.name] || ""}
                onChange={(e) => handleChange(e, setSalary)}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
          ))}
        </div>
		<div className="col-span-full text-right mt-2">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Salary
			</Button>
		  </div>
		</form>
      </section>

      {/* ====================== References ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">References</h3>
        <form className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: "Name", name: "name" },
            { label: "Designation", name: "designation" },
            { label: "Organisation", name: "organisation" },
            { label: "Address", name: "address" },
            { label: "Phone", name: "phone" },
            { label: "Email", name: "email" },
            { label: "Relation", name: "relation" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">
                {f.label}
              </label>
              <input
                name={f.name}
                value={refForm[f.name] || ""}
                onChange={(e) => handleChange(e, setRefForm)}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
          ))}

          <div className="col-span-full flex justify-end gap-3">
            {editRefIndex !== null && (
              <Button variant="outline" onClick={() => setEditRefIndex(null)}>
                Cancel
              </Button>
            )}
            <Button
              className="bg-blue-600 text-white"
			  onClick={async (e) => {
				e.preventDefault();

				try {
				  const payload = new FormData();
				  Object.entries(refForm).forEach(([key, value]) => {
					payload.append(key, value);
				  });

				  // Add authentication info
				  payload.append("userId", import.meta.env.VITE_API_USER);
				  payload.append("password", import.meta.env.VITE_API_PASS);
				  payload.append("user", secureLocalStorage.getItem("user"));
				  payload.append("token", secureLocalStorage.getItem("token"));
				  if (editRefIndex !== null) {
					// Update existing language
					const existingLang = references[editRefIndex];
					
					const api = axios.create({
						baseURL: import.meta.env.VITE_API_BASE_URL,
						headers: { "Content-Type": "application/json" },
					});
					const res = await api.post("/update-reference", payload);
					
					setReferences(res.data || []);
					setEditRefIndex(null);
				  } else {
					const api = axios.create({
						baseURL: import.meta.env.VITE_API_BASE_URL,
						headers: { "Content-Type": "application/json" },
					});
					const res = await api.post("/update-reference", payload);
					
					setReferences(res.data || []);
				  }

				  setRefForm({});
				} catch (error) {
				  console.error("Error saving Reference:", error);
				  alert("Failed to save Reference!");
				}
			  }}

            >
              {editRefIndex !== null ? "Update Reference" : "Add Reference"}
            </Button>
          </div>
        </form>

        <div className="mt-4 space-y-3">
          {references.map((ref, i) => (
            <div
              key={i}
              className="p-4 bg-blue-50 rounded-lg flex justify-between shadow-sm"
            >
              <div>
                <p className="font-semibold text-blue-800">{ref.name}</p>
                <p className="text-sm text-gray-600">{ref.designation}</p>
                <p className="text-sm text-gray-600">{ref.organisation}</p>
                <p className="text-sm text-gray-600">{ref.phone}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditRefIndex(i);
                  setRefForm(ref);
                }}
              >
                Edit
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* ====================== Languages ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">Languages</h3>
        <form className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Language", name: "language" },
            { label: "Writing", name: "writing", type: "select" },
            { label: "Reading", name: "reading", type: "select" },
            { label: "Speaking", name: "speaking", type: "select" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">
                {f.label}
              </label>
              {f.type === "select" ? (
                <select
                  name={f.name}
                  value={langForm[f.name] || ""}
                  onChange={(e) => handleChange(e, setLangForm)}
                  className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
                >
                  <option value="">Select</option>
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                  <option value="Native">Native</option>
                </select>
              ) : (
                <input
                  name={f.name}
                  value={langForm[f.name] || ""}
                  onChange={(e) => handleChange(e, setLangForm)}
                  className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
                />
              )}
            </div>
          ))}

          <div className="col-span-full flex justify-end gap-3">
            {editLangIndex !== null && (
              <Button variant="outline" onClick={() => setEditLangIndex(null)}>
                Cancel
              </Button>
            )}
            <Button
			  className="bg-blue-600 text-white"
			  onClick={async (e) => {
				e.preventDefault();

				try {
				  const payload = new FormData();
				  Object.entries(langForm).forEach(([key, value]) => {
					payload.append(key, value);
				  });

				  // Add authentication info
				  payload.append("userId", import.meta.env.VITE_API_USER);
				  payload.append("password", import.meta.env.VITE_API_PASS);
				  payload.append("user", secureLocalStorage.getItem("user"));
				  payload.append("token", secureLocalStorage.getItem("token"));
				  if (editLangIndex !== null) {
					// Update existing language
					const existingLang = languages[editLangIndex];
					
										
					const api = axios.create({
						baseURL: import.meta.env.VITE_API_BASE_URL,
						headers: { "Content-Type": "application/json" },
					});
					const res = await api.post("/update-language", payload);
					
					setLanguages(res.data);
					setEditLangIndex(null);
				  } else {
					const api = axios.create({
						baseURL: import.meta.env.VITE_API_BASE_URL,
						headers: { "Content-Type": "application/json" },
					});
					const res = await api.post("/update-language", payload);					
					setLanguages(res.data);
				  }

				  setLangForm({});
				} catch (error) {
				  console.error("Error saving language:", error);
				  alert("Failed to save language!");
				}
			  }}
			>
			  {editLangIndex !== null ? "Update Language" : "Add Language"}
			</Button>
            
          </div>
        </form>

        <div className="mt-4 space-y-3">
          {languages.map((lang, i) => (
            <div
              key={i}
              className="p-4 bg-blue-50 rounded-lg flex justify-between shadow-sm"
            >
              <div>
                <p className="font-semibold text-blue-800">{lang.language}</p>
                <p className="text-sm text-gray-600">
                  W: {lang.writing}, R: {lang.reading}, S: {lang.speaking}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditLangIndex(i);
                  setLangForm(lang);
                }}
              >
                Edit
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* ====================== Extracurricular Activities ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">
          Extracurricular Activities
        </h3>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("activities", activities);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/update-activities`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Activities updated!");
			} catch (error) {
			  console.error("Error updating Activities:", error);
			  alert("Failed to update Activities!");
			}
		  }}
		  className="space-y-2"
		>
		
		<Editor
			apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
			value={activities || ""}
			onEditorChange={setActivities}
			init={{
			  height: 300,
			  menubar: false,
			  plugins: [
				"advlist autolink lists link charmap preview anchor",
				"searchreplace visualblocks code fullscreen",
				"insertdatetime media table help wordcount",
			  ],
			  toolbar:
				"undo redo | formatselect | bold italic underline | bullist numlist | alignleft aligncenter alignright | link | code | removeformat",
			  content_style:
				"body { font-family:Inter,Arial,sans-serif; font-size:14px; }",
			}}
		/>
		<div className="text-right">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Activities
			</Button>
		  </div>
		</form>

      </section>

      {/* ====================== Declaration ====================== */}
      <section>
        <h3 className="text-xl font-semibold text-[#1A2B63] mb-2">
          Declaration of Statement
        </h3>
		<form
		  onSubmit={async (e) => {
			e.preventDefault();

			try {
			  const payload = new FormData();
			  payload.append("declaration", declaration);
			  payload.append("userId", import.meta.env.VITE_API_USER);
			  payload.append("password", import.meta.env.VITE_API_PASS);
			  payload.append("user", secureLocalStorage.getItem("user"));
			  payload.append("token", secureLocalStorage.getItem("token"));
			  await axios.patch(`${import.meta.env.VITE_API_BASE_URL}/update-declaration`, payload, {
				headers: { "Content-Type": "application/json" },
			  });

			  alert("Declaration updated!");
			} catch (error) {
			  console.error("Error updating Declaration:", error);
			  alert("Failed to update Declaration!");
			}
		  }}
		  className="space-y-2"
		>
		
        <input
          value={declaration}
          onChange={(e) => setDeclaration(e.target.value)}
          className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
        />
        <p className="text-sm text-gray-500 mt-2 italic">
          * Your digital signature (E-Sign) or name can serve as a declaration.
        </p>
		<div className="text-right">
			<Button type="submit" className="bg-blue-600 text-white">
			  Update Declaration
			</Button>
		  </div>
		</form>
      </section>
    </motion.div>
  );
};

export default AdditionalInfo;
